export class CanvasManager {
  readonly canvas: HTMLCanvasElement;
  readonly overlay: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
  readonly overlayCtx: CanvasRenderingContext2D;
  private container: HTMLElement;

  constructor(canvas: HTMLCanvasElement, overlay: HTMLCanvasElement, container: HTMLElement) {
    this.canvas = canvas;
    this.overlay = overlay;
    this.container = container;
    this.ctx = canvas.getContext('2d', { willReadFrequently: true })!;
    this.overlayCtx = overlay.getContext('2d')!;
  }

  setSize(width: number, height: number): void {
    this.canvas.width = width;
    this.canvas.height = height;
    this.overlay.width = width;
    this.overlay.height = height;
    this.updateDisplaySize();
  }

  private static readonly MAX_DISPLAY_HEIGHT = 600;

  private updateDisplaySize(): void {
    const cw = this.container.clientWidth;
    const iw = this.canvas.width;
    const ih = this.canvas.height;

    // Scale to fit container width and a fixed max height, never upscale
    const scale = Math.min(cw / iw, CanvasManager.MAX_DISPLAY_HEIGHT / ih, 1);
    const displayW = Math.round(iw * scale);
    const displayH = Math.round(ih * scale);

    this.canvas.style.width = `${displayW}px`;
    this.canvas.style.height = `${displayH}px`;
    this.overlay.style.width = `${displayW}px`;
    this.overlay.style.height = `${displayH}px`;
  }

  drawImageData(imageData: ImageData): void {
    // Only resize canvas buffer when dimensions actually change
    // (resizing clears the canvas and can cause layout feedback loops)
    if (this.canvas.width !== imageData.width || this.canvas.height !== imageData.height) {
      this.canvas.width = imageData.width;
      this.canvas.height = imageData.height;
      this.overlay.width = imageData.width;
      this.overlay.height = imageData.height;
    }
    this.ctx.putImageData(imageData, 0, 0);
    this.updateDisplaySize();
  }

  getImageData(): ImageData {
    return this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
  }

  show(): void {
    this.canvas.classList.add('visible');
  }

  showOverlay(): void {
    this.overlay.classList.add('visible');
  }

  hideOverlay(): void {
    this.overlay.classList.remove('visible');
    this.overlayCtx.clearRect(0, 0, this.overlay.width, this.overlay.height);
  }

  clearOverlay(): void {
    this.overlayCtx.clearRect(0, 0, this.overlay.width, this.overlay.height);
  }

  /** Convert mouse event coords on overlay to image-space coords */
  toImageCoords(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.overlay.getBoundingClientRect();
    const scaleX = this.overlay.width / rect.width;
    const scaleY = this.overlay.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  }

  refreshDisplay(): void {
    this.updateDisplaySize();
  }
}
